import type { en } from "./en";

// Swedish translations. Missing keys fall back to English via i18next.
export const sv: typeof en = {
  common: {
    home: "Hem",
    no_scores: "Inga poäng än",
    points_count_one: "{{count}} poäng",
    points_count_other: "{{count}} poäng",
    questions_count_one: "{{count}} fråga",
    questions_count_other: "{{count}} frågor",
  },
  home: {
    placeholder: "Spelkod",
    enter: "Gå med",
    error_invalid_code:
      "Skriv in den 6-teckens spelkod du fått av läraren.",
    footer: "Starta en Binogi Buzz från Binogi.com.",
    language_label: "Språk",
  },
  errors: {
    generic: "Något gick fel. Försök igen.",
    stale_client: "Sidan är inaktuell – ladda om den.",
    session_not_found: "Omgången kunde inte hittas.",
    session_not_open: "Den här omgången är avslutad.",
    session_not_active: "Frågan eller omgången har redan avslutats.",
    not_teacher: "Bara läraren kan göra det.",
    already_answered: "Du har redan svarat på den här frågan.",
    wrong_question_index: "Frågan har inte börjat än.",
    name_taken: "Det namnet är redan taget. Prova ett annat.",
    character_busy: "Vi kunde inte ge dig en karaktär just nu – försök igen.",
  },
  host: {
    input: {
      heading: "Starta en Buzz",
      lead:
        "Skriv in lektionskoden eleverna jobbar med. Vi laddar in frågorna och skapar en spelomgång du kan visa på tavlan.",
      lesson_code_label: "Lektionskod",
      placeholder_code: "BIH750",
      load_button: "Ladda lektion",
      try_hint: "Testa:",
      error_invalid_code: "Skriv in en Binogi-lektionskod, t.ex. BIH750.",
      error_not_found: "Ingen lektion hittades med koden {{code}}.",
      error_unreachable: "Kunde inte nå lektionsservern. Kontrollera anslutningen och försök igen.",
      error_no_questions: "Lektionen {{code}} har inga frågor som Buzz kan använda.",
      error_no_level_questions: "Lektionen {{code}} har inga frågor på den valda nivån.",
    },
    loading: "Laddar lektion…",
    placeholder_title: "Starta en Binogi Buzz",
    tab_details: "Detaljer",
    tab_questions: "Frågor",
    settings: "Inställningar",
    number_of_students: "Antal elever",
    fewer_students: "Färre elever",
    more_students: "Fler elever",
    player_names: "Spelarnamn",
    player_anonymous: "Anonyma",
    player_self_chosen: "Eleverna väljer",
    language: "Språk",
    language_teacher: "Läraren väljer",
    language_students: "Eleverna väljer",
    time_per_question: "Tid per fråga",
    time_option: "{{seconds}} s",
    start_button: "Starta omgång",
    start_disabled: "Välj klasstorlek för att starta",
    creating: "Skapar omgång…",
    error_no_questions: "Behåll minst en fråga innan du startar.",
    error_create_failed: "Kunde inte skapa omgång",
    error_load_failed: "Kunde inte ladda lektion",
    no_session: "Ingen omgång pågår.",
    back_to_review: "Tillbaka till frågorna",
    errors: {
      dismiss: "Stäng",
      refresh: "Ladda om",
      attach_failed: "Den här skärmen kunde inte ansluta som lärare.",
      unreachable: "Kunde inte nå servern. Ladda om sidan för att försöka igen.",
      no_token: "Den här fliken kan inte längre styra quizet. Öppna Buzz från din lektion för att starta en ny omgång.",
      taken_over: "Quizet styrs nu från en annan skärm.",
      stale_question: "Buzz uppdaterades under quizet, så den här skärmen kan inte fortsätta det. Ladda om sidan för att fortsätta quizet där klassen är.",
      question_moved_on: "Quizet har redan gått vidare från den här frågan, så den här skärmen ligger efter. Ladda om sidan för att fortsätta quizet där klassen är.",
    },
    entry: {
      opening: "Öppnar lärarvyn…",
      token_title: "Den här lärarlänken kan inte öppnas",
      token_body: "Länken saknar lärarnyckel eller har gått ut. Öppna Buzz från din lektion för att skapa en ny omgång.",
      not_found_title: "Omgången hittades inte",
      not_found_body: "Den här quizomgången finns inte längre. Skapa en ny för att fortsätta.",
      back: "Tillbaka till start",
    },
    questions: {
      drag_aria: "Dra för att ändra ordning",
      remove_aria: "Ta bort fråga",
      empty: "Inga frågor kvar i den här omgången.",
      add: "Lägg till",
      add_aria: "Lägg till fråga: {{text}}",
      add_all: "Lägg till alla",
      add_all_n: "Lägg till alla {{count}} frågor",
      more_one: "{{count}} fråga till i lektionen",
      more_other: "{{count}} frågor till i lektionen",
      level_easy: "Lätt",
      level_medium: "Medel",
      level_hard: "Svår",
      multi: "Flerval",
    },
    lobby: {
      go_to: "Gå till <strong>buzz.binogi.com</strong> och skriv in:",
      or_qr: "…eller skanna QR-koden",
      students_joined_of: "{{joined}} av {{expected}} elever har gått med",
      students_joined_one: "{{count}} elev har gått med",
      students_joined_other: "{{count}} elever har gått med",
      waiting: "Väntar på att eleverna ska gå med…",
      start_game: "Starta spel",
      connected: "Ansluten",
      connecting: "Ansluter…",
      copy_link: "Kopiera länk",
      copy_link_copied: "Kopierad!",
    },
    question: {
      header: "Fråga {{index}} av {{total}}",
      level: "Nivå {{level}}",
      none: "Ingen aktiv fråga.",
      answered: "Svarade",
      top_n: "Topp {{count}}",
      answers_heading: "Svar",
      end_question: "Avsluta fråga",
      next_question: "Nästa fråga",
      see_results: "Se resultatet",
    },
    results: {
      heading: "Resultat",
      all_players: "Alla spelare",
      end_session: "Avsluta omgång",
      new_session: "Ny omgång",
      none: "Inga spelare i den här omgången.",
    },
  },
  join: {
    code_label: "Spelkod",
    name_label: "Ditt namn",
    name_placeholder: "Skriv ditt namn",
    join_button: "Gå med",
    joining: "Ansluter…",
    checking: "Kontrollerar…",
    character_hint:
      "Du får ett kul Binogi-karaktärsnamn när du går med.",
    error_not_found:
      "Den koden är inte en aktiv omgång ännu. Fråga din lärare.",
    error_lookup_failed: "Kunde inte hitta omgången",
    error_code_required: "Skriv in den 6-teckens koden.",
    error_name_required: "Skriv ditt namn.",
  },
  play: {
    superseded: "Du är ansluten i en annan flik — stäng den för att fortsätta spela här.",
    back_to_join: "Gå med i ett spel",
    no_round: "Ingen omgångsdata.",
    not_joined: "Du har inte gått med i någon omgång.",
    no_question: "Ingen aktiv fråga.",
    keep_awake_hint: "Tryck på skärmen så att den inte släcks",
    waiting: {
      you_are: "Du är",
      get_ready_named: "Gör dig redo, {{name}}!",
      get_ready: "Gör dig redo för quizet!",
      in_room_label: "Elever i rummet",
      in_room_one: "{{count}} elev i rummet",
      in_room_other: "{{count}} elever i rummet",
    },
    question: {
      progress: "{{index}} / {{total}}",
      answered: "Svarat!",
      times_up: "Tiden är ute",
      submit: "Svara",
      submit_n: "Svara med {{count}} val",
    },
    between: {
      correct: "Rätt!",
      wrong: "Inte riktigt!",
      times_up: "Tiden är ute",
      points_gained: "+{{count}} poäng",
      your_score: "Dina poäng",
      your_rank: "Plats {{rank}}",
      top_n: "Topp {{count}}",
      waiting_next: "Väntar på nästa fråga…",
    },
    finished: {
      heading: "Tack för att du spelade Binogi Buzz!",
      your_final_rank: "Din placering",
      top_n: "Topp {{count}}",
      none: "Inga resultat att visa.",
    },
  },
  characters: {
    heading: "Binogi-karaktärer",
    lead:
      "{{count}} elever får en karaktär när de går med, i den här ordningen.",
  },
};
